import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import FileUploader from '../../components/FileUploader';
import VideoAdModal from '../../components/VideoAdModal';
import { conversionAPI } from '../../services/api';
import { FiDownload, FiLoader, FiFileText } from 'react-icons/fi';
import ToolContentSection from '../../components/ToolContentSection';

const ExcelToPdf = () => {
  const { user } = useAuth();
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [convertedFile, setConvertedFile] = useState(null);
  const [showAdModal, setShowAdModal] = useState(false);

  const handleConvert = async () => {
    if (!selectedFile) {
      toast.error('Please select an Excel file');
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('storageType', 'temporary');

    try {
      const data = await conversionAPI.convertFile('excel-to-pdf', formData);
      setConvertedFile(data);

      if (!user || !user.isPremium) {
        setShowAdModal(true);
      } else {
        toast.success('Conversion complete!');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Conversion failed');
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="relative min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Excel to PDF Converter</h1>
          <p className="text-lg text-gray-600">Convert Excel spreadsheets (.xls, .xlsx) to PDF format</p>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8">
          <FileUploader
            acceptedFiles={{
              'application/vnd.ms-excel': ['.xls'],
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
            }}
            onFilesSelect={(files) => setSelectedFile(files[0])}
            maxFiles={1}
            maxSize={user?.isPremium ? 50 * 1024 * 1024 : 10 * 1024 * 1024}
          />

          {selectedFile && (
            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <FiFileText className="text-blue-600" size={24} />
                  <div>
                    <p className="font-medium text-gray-900">{selectedFile.name}</p>
                    <p className="text-sm text-gray-600">
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleConvert}
                  disabled={loading}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 flex items-center space-x-2"
                >
                  {loading ? (
                    <>
                      <FiLoader className="animate-spin" />
                      <span>Converting...</span>
                    </>
                  ) : (
                    <span>Convert to PDF</span>
                  )}
                </button>
              </div>
            </div>
          )}

          {convertedFile && user && user.isPremium && (
            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
              <p className="font-medium text-gray-900">{convertedFile.fileName}</p>
              <a
                href={conversionAPI.downloadFile(convertedFile.fileName)}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center"
              >
                <FiDownload className="mr-2" />
                Download
              </a>
            </div>
          )}
        </div>

        {/* Rich SEO Content */}
        <ToolContentSection
          title="Convert Excel Sheets to PDF"
          content={{
            introduction: [
              "Sending an Excel spreadsheet often results in messy, unreadable tables when the recipient opens it on a phone. The solution? Convert your Excel sheets to PDF.",
              "Our Excel to PDF tool takes your spreadsheets (.xls or .xlsx) and formats them into clean, professional PDF pages. You can ensure that your financial reports, invoices, and data tables are presented exactly as intended, without the risk of accidental edits."
            ],
            benefits: [
              "Fit-to-page formatting options",
              "Prevents accidental data modification",
              "Preserves table structure and cell borders",
              "Professional presentation for reports",
              "Works with both XLS and XLSX files",
              "Fast and secure cloud conversion"
            ],
            howToSteps: [
              "Select your Excel file (.xlsx or .xls).",
              "Choose your preferred storage method.",
              "Click 'Convert to PDF'.",
              "Our engine formats your sheets for the page.",
              "Download your professional PDF report."
            ],
            faqs: [
              {
                question: "How are pages split?",
                answer: "Our engine attempts to fit columns to the page width where possible. For wide spreadsheets, the content will flow to subsequent pages."
              },
              {
                question: "Can I convert multiple tabs?",
                answer: "Yes, the tool converts all active sheets in your workbook into a single, multi-page PDF document."
              },
              {
                question: "Is my financial data safe?",
                answer: "Security is our priority. Your spreadsheets are processed over an encrypted connection and are strictly deleted from our servers after 1 hour (for free users)."
              }
            ]
          }}
        />

        {/* Features */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="font-semibold text-gray-900 mb-2">Preserve Layout</h3>
            <p className="text-sm text-gray-600">
              Maintains spreadsheet formatting, formulas, and cell styling
            </p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="font-semibold text-gray-900 mb-2">Multiple Sheets</h3>
            <p className="text-sm text-gray-600">
              Converts all sheets in your workbook to a single PDF
            </p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="font-semibold text-gray-900 mb-2">Secure</h3>
            <p className="text-sm text-gray-600">
              Files are automatically deleted after 1 hour for your privacy
            </p>
          </div>
        </div>
      </div>

      {showAdModal && convertedFile && (
        <VideoAdModal
          isOpen={showAdModal}
          onClose={() => setShowAdModal(false)}
          onAdComplete={() => toast.success('Conversion complete!')}
          downloadUrl={conversionAPI.downloadFile(convertedFile.fileName)}
          fileName={convertedFile.fileName}
        />
      )}
    </div>
  );
};

export default ExcelToPdf;
